import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import LoadingElement from '../../loading-element';
import { ReactComponent as FolderIcon } from '../../../shared/icons/folder-icon.svg';
import { ReactComponent as SearchIcon } from '../../../shared/icons/search-icon.svg';
import { ReactComponent as ChevronRightIcon } from '../../../shared/icons/chevron-right.svg';
import { ReactComponent as BackIcon } from '../../../shared/icons/chevron-left.svg';
import styles from './DealPicker.module.scss';

// Query to get deals with document counts
const GET_DEALS_WITH_COUNTS = gql`
  query GetDealsWithCounts($teamId: ID!) {
    dealsForTeam(teamId: $teamId) {
      id
      title
      documentsCount
    }
  }
`;

interface Deal {
  id: string;
  title: string;
  documentsCount: number;
}

interface DealPickerProps {
  onDealSelect: (dealId: string, dealName: string) => void;
  onBack: () => void;
  teamId?: string;
  defaultDealId?: string;
}

export const DealPicker: React.FC<DealPickerProps> = ({
  onDealSelect,
  onBack,
  teamId = '1', // TODO: Get from team context
  defaultDealId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, loading, error } = useQuery(GET_DEALS_WITH_COUNTS, {
    variables: { teamId },
    skip: !teamId,
  });

  const filteredDeals = useMemo(() => {
    if (!data?.dealsForTeam) return [];

    const deals = data.dealsForTeam as Deal[];

    if (!searchQuery) {
      // If there's a default deal, put it at the top
      if (defaultDealId) {
        const defaultDeal = deals.find(d => d.id === defaultDealId);
        const otherDeals = deals.filter(d => d.id !== defaultDealId);
        return defaultDeal ? [defaultDeal, ...otherDeals] : deals;
      }
      return deals;
    }

    return deals.filter(deal =>
      deal.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery, defaultDealId]);

  const handleDealSelect = (deal: Deal) => {
    onDealSelect(deal.id, deal.title);
  };

  if (loading) {
    return (
      <div className={styles.dealPicker}>
        <div className={styles.loadingContainer}>
          <LoadingElement />
          <p>Loading deals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.dealPicker}>
        <div className={styles.errorContainer}>
          <p>Error loading deals. Please try again.</p>
          <button onClick={onBack} className={styles.backButton}>
            <BackIcon /> Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dealPicker}>
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButton} aria-label="Go back">
          <BackIcon />
          <span>Back</span>
        </button>
        <div className={styles.divider}>|</div>
        <h3>Select a Deal</h3>
      </div>

      <div className={styles.searchBar}>
        <SearchIcon className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search deals..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
          autoFocus
        />
      </div>

      <div className={styles.dealList}>
        {filteredDeals.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <p>No deals found matching "{searchQuery}"</p>
            ) : (
              <p>No deals available</p>
            )}
          </div>
        ) : (
          filteredDeals.map(deal => (
            <button
              key={deal.id}
              className={styles.dealItem}
              onClick={() => handleDealSelect(deal)}
              aria-label={`Select ${deal.title}`}
            >
              <FolderIcon className={styles.dealIcon} />
              <div className={styles.dealInfo}>
                <span className={styles.dealTitle}>
                  {deal.title}
                  {deal.id === defaultDealId && <span className={styles.currentDealBadge}> (Current Deal)</span>}
                </span>
                <span className={styles.dealMeta}>
                  {deal.documentsCount} {deal.documentsCount === 1 ? 'document' : 'documents'}
                </span>
              </div>
              <ChevronRightIcon className={styles.chevron} />
            </button>
          ))
        )}
      </div>
    </div>
  );
};